/**
 * AbilityVFX — six effect archetypes keyed on `hit.kind`, three beats each.
 *
 * The archetype axis is the same one the cast animations use (CHARACTERS.md
 * §4b) and the same one the reference art is keyed on: twenty bespoke effects
 * drift the moment somebody adds an ability; six archetypes cover every
 * ability by construction, because the thing being drawn is what the ability
 * DOES to space.
 *
 * Every effect has three beats, timed by the ability's OWN numbers:
 *
 *   ANTICIPATION over `windupMs`  — something gathers on the caster. This is
 *     the telegraph an opponent reads; a zero-windup ability gets none,
 *     because drawing anticipation for the unreactable lies the other way.
 *   STRIKE at windup end          — the shape the ability occupies, at its
 *     real size, exactly when the sim resolves it. The previous version
 *     played the strike at CAST — One More Thing's shockwave fired 400ms
 *     before its damage, which is misinformation in a game with money on it.
 *   DISSIPATION over `recoveryMs` — the strike decays while the caster is
 *     committed.
 *
 * Geometry discipline is inherited from Telegraphs.js: any shape that claims
 * an area is drawn from the roster's `HitShape` numbers, and nearest-target
 * abilities (chain, swap) never get an area burst — the burst would promise
 * AoE damage that does not exist. Flourish (shards, sparks) is approximate;
 * the shapes are not.
 *
 * The LOOK is `vfx/CelShapes.js`: hard cel bands, white core → saturated body
 * → dark rim, chunky shard debris — the reference-art language, computed in
 * TSL so it tints per character and stays crisp at any radius.
 */

import * as THREE from 'three/webgpu';
import { Fn, uv, float, smoothstep, uniform } from 'three/tsl';
import { CHARACTERS, abilityAnchor } from '@chaos/protocol';
import {
  forwardFromYaw,
  GROUND_ZONE_OFFSET,
  PROJECTILE_LAUNCH_SIN,
  PROJECTILE_LAUNCH_COS,
  PROJECTILE_SPAWN_OFFSET,
  PLAYER_HALF_HEIGHT,
  DT,
} from '@chaos/sim';
import { spawnParticles } from '../vfx/ScreenEffects.js';
import {
  burstRing,
  slashArc,
  starFlash,
  hexRing,
  hexShell,
  lineWall,
  zoneRing,
  boltBeam,
  flatOnGround,
} from '../vfx/CelShapes.js';
import { getCurrentTier } from '../PostProcessing.js';
import { characterColorHex } from '../characters/CharacterModels.js';
// Namespace import on purpose: `setFlashAmount` may not exist yet (it lands with
// the ToonMaterials flash-uniform work) and a named import of a missing export
// is a hard error under ESM. The guard in flashHitTarget makes this a no-op
// until the helper appears.
import * as ToonMaterials from '../ToonMaterials.js';
import { session, localPlayer, remotePlayers } from '../state.js';
import { DECAL_LIFT, DECAL_STACK } from './decals.js';

let _scene = null;
/** Optional caster-position resolver: (casterId, outVec3) => boolean. The
 *  character mockup injects one so effects ride ITS bodies; the game falls
 *  back to state.js (local renderPos / remote meshes). */
let _resolveCaster = null;

/** Live transient meshes: { mesh, born, life, update?, sharedGeo }. */
const _live = [];
/** Scheduled strike beats: { at, fire }. Fired by updateAbilityVFX — the
 *  strike must land on the wind-up clock, not on a setTimeout the tab can
 *  starve. */
const _pending = [];

/** Ground shapes sit above telegraph decals, which sit at DECAL_LIFT. */
const FX_LIFT = DECAL_LIFT + DECAL_STACK;

// ─── Quality tiers ──────────────────────────────────────────────
//
// Particle COUNTS scale with tier; the shapes themselves stay — they are the
// information. `luxe()` gates the decorative extras (secondary rings, barrage
// shell streaks) that a low-tier phone can drop without losing meaning.

const TIER_MUL = { ultra: 1, high: 0.8, medium: 0.55, low: 0.35 };

function nCount(n) {
  return Math.max(2, Math.round(n * (TIER_MUL[getCurrentTier()] ?? 1)));
}

function luxe() {
  const t = getCurrentTier();
  return t === 'ultra' || t === 'high';
}

// ─── Body anchors ───────────────────────────────────────────────
//
// Body-anchored casts: { group, casterId, freezeAt, holdUntil }. A body
// ability RESOLVES from the caster's live position, so its VFX must ride the
// caster's rendered transform. `holdUntil` keeps the anchor alive through the
// wind-up even if every anticipation mesh has expired — the strike beat still
// needs somewhere to spawn.

const _anchors = [];
/** Set while a body-anchored beat builds, so track() parents into its group. */
let _buildAnchor = null;
const _anchorScratch = new THREE.Vector3();

export function initAbilityVFX(scene, casterResolver = null) {
  _scene = scene;
  _resolveCaster = casterResolver;
}

function track(mesh, life, update) {
  mesh.frustumCulled = false;
  if (_buildAnchor) {
    _buildAnchor.group.add(mesh);
    // The primitives compute world positions; the group already carries the
    // anchor origin, so re-express the mesh in caster-local space. The group
    // never rotates, so orientations survive unchanged.
    const o = _buildAnchor.group.position;
    mesh.position.x -= o.x;
    mesh.position.y -= o.y;
    mesh.position.z -= o.z;
  } else {
    _scene.add(mesh);
  }
  _live.push({
    mesh,
    born: performance.now(),
    life,
    update,
    sharedGeo: mesh.isSprite || mesh.userData.sharedGeo === true,
  });
  return mesh;
}

/** The caster's rendered transform — the same source the character mesh draws
 *  from, so the VFX can never be somewhere the body visibly is not. */
function casterRenderPos(casterId, out) {
  if (_resolveCaster) return _resolveCaster(casterId, out) ? out : null;
  if (casterId === session.userId) return out.copy(localPlayer.renderPos);
  const mesh = remotePlayers.get(casterId);
  return mesh ? out.copy(mesh.position) : null;
}

/**
 * No walk speed covers this in one frame (fastest kit at 15fps is ~1.4m), but
 * the local prediction applies Blink Strike's 9m selfMove as a single-frame
 * jump. Squared metres.
 */
const TELEPORT_JUMP2 = 2.5 * 2.5;

/**
 * `freezeAfterMs`: Blink Strike's line follows the body only up to the dash,
 * then freezes — the sim sweeps the dash from the caster's live position AT
 * RESOLVE, so the effect must be seated where the sweep started. Two triggers,
 * first one wins: the wind-up clock, and a single-frame teleport of the body
 * (the local prediction resolves the dash BEFORE the wall clock says so).
 */
function beginBodyAnchor(abilityId, casterId, origin, freezeAfterMs, holdMs) {
  const group = new THREE.Group();
  group.name = `vfx-anchor:${abilityId}:${casterId}`;
  group.position.set(origin.x, origin.y, origin.z);
  _scene.add(group);
  const now = performance.now();
  const anchor = {
    group,
    casterId,
    freezeAt: freezeAfterMs === Infinity ? Infinity : now + freezeAfterMs,
    holdUntil: now + holdMs,
  };
  _anchors.push(anchor);
  return anchor;
}

function updateAnchors(now) {
  for (let i = _anchors.length - 1; i >= 0; i--) {
    const a = _anchors[i];
    // All child effects expired AND no beat is still waiting to spawn into it.
    if (a.group.children.length === 0 && now >= a.holdUntil) {
      _scene.remove(a.group);
      _anchors.splice(i, 1);
      continue;
    }
    if (now >= a.freezeAt) continue;
    // A dead/vanished caster keeps the last position — the effect finishes
    // where the body was last drawn instead of snapping to a stale origin.
    if (!casterRenderPos(a.casterId, _anchorScratch)) continue;
    // Finite-freeze anchors (the dash line) also freeze on a teleport-sized
    // jump: that IS the dash, seen through prediction ahead of the clock.
    if (
      a.freezeAt !== Infinity &&
      a.group.position.distanceToSquared(_anchorScratch) > TELEPORT_JUMP2
    ) {
      a.freezeAt = 0;
      continue;
    }
    a.group.position.copy(_anchorScratch);
  }
}

// ─── Animated shape spawners ────────────────────────────────────
//
// Each wraps a CelShapes builder with its motion curve and lifetime. `hold`
// then fade is the house rule for anything that marks live danger — an
// indicator that fades the whole time reads as "already gone" while it kills.

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

/** Fade profile: full strength until `holdFrac`, then out. */
const holdFade = (t, holdFrac = 0.6) => (t < holdFrac ? 1 : 1 - (t - holdFrac) / (1 - holdFrac));

/** The exact-radius burst: expands 0→radius fast, holds, dies over the tail. */
function popRing(x, y, z, radius, colorHex, life = 480, { reverse = false } = {}) {
  const bandStart = 1 - Math.min(0.42, Math.max(0.16, 2.1 / radius));
  const { mesh, u } = burstRing(colorHex, { bandStart });
  flatOnGround(mesh, x, y, z);
  mesh.renderOrder = 6;
  const grow = Math.min(0.5, 180 / life);
  return track(mesh, life, (m, t) => {
    const k = reverse
      ? 1 - easeOutCubic(Math.min(t / 0.85, 1)) // implosion: radius → 0
      : t < grow
        ? easeOutCubic(t / grow) // burst: 0 → radius, then STOP (the true edge)
        : 1;
    m.scale.setScalar(Math.max(0.06, radius * k));
    u.fade.value = holdFade(t, reverse ? 0.7 : 0.45);
  });
}

/** The billboarded star flash — impacts, muzzles, charge glints. */
function popStar(x, y, z, size, colorHex, life = 240, { additive = false } = {}) {
  const { mesh, u } = starFlash(colorHex, { additive });
  mesh.position.set(x, y, z);
  mesh.renderOrder = 8;
  return track(mesh, life, (m, t) => {
    m.scale.setScalar(size * (0.55 + 0.45 * easeOutCubic(Math.min(t * 3, 1))));
    u.fade.value = holdFade(t, 0.35);
  });
}

/** A star that GROWS over the wind-up — the universal charge glint. */
function chargeStar(x, y, z, size, colorHex, windupMs) {
  const { mesh, u } = starFlash(colorHex, { additive: true });
  mesh.position.set(x, y, z);
  mesh.renderOrder = 8;
  return track(mesh, windupMs + 60, (m, t) => {
    const k = Math.min(t * (windupMs + 60) / windupMs, 1);
    m.scale.setScalar(Math.max(0.05, size * easeOutCubic(k)));
    u.spin.value += 0.06;
    u.fade.value = t > 0.85 ? (1 - t) / 0.15 : 0.9;
  });
}

/** A ring that CONTRACTS from the ability's radius onto the caster — the
 *  sphere archetype's anticipation, drawn at the radius it will strike at. */
function gatherRing(x, y, z, radius, colorHex, windupMs) {
  const bandStart = 1 - Math.min(0.42, Math.max(0.16, 2.1 / radius));
  const { mesh, u } = burstRing(colorHex, { bandStart });
  flatOnGround(mesh, x, y, z);
  mesh.renderOrder = 6;
  return track(mesh, windupMs + 60, (m, t) => {
    const k = Math.min(t * (windupMs + 60) / windupMs, 1);
    m.scale.setScalar(Math.max(0.06, radius * (1 - easeOutCubic(k) * 0.9)));
    u.fade.value = 0.35 + 0.4 * k;
  });
}

/** A vertical column: teleports, ult charges, eruptions. Kept from the old
 *  vocabulary — a pillar is the one shape that reads at ANY camera pitch. */
function pillar(x, y, z, radius, height, colorHex, life = 460) {
  const geo = new THREE.CylinderGeometry(radius, radius * 0.7, height, 12, 1, true);
  const mat = new THREE.MeshBasicNodeMaterial({ color: colorHex });
  mat.transparent = true;
  mat.depthWrite = false;
  mat.side = THREE.DoubleSide;
  mat.blending = THREE.AdditiveBlending;
  // Full-opacity additive over a light tint reads as a solid milky cup from
  // the 55° camera (seen on CUDA's discharge in the mockup) — a column of
  // LIGHT, not a drinking glass, is the read this shape is for.
  mat.opacity = 0.55;
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set(x, y + height / 2, z);
  return track(mesh, life, (m, t) => {
    m.material.opacity = (1 - t) * 0.55;
    m.scale.set(1 + t * 0.35, 1, 1 + t * 0.35);
  });
}

/** A translucent dome — Deindexed's suppression field. The opacity lives at
 *  the RIM and dies toward the top: a flat additive fill at radius 10 washes
 *  the entire screen purple (seen in the mockup, not guessed), while a shell
 *  that is only visible edge-on reads as a boundary you can look into. */
function dome(x, y, z, radius, colorHex, life) {
  const geo = new THREE.SphereGeometry(radius, 20, 12, 0, Math.PI * 2, 0, Math.PI / 2);
  const mat = new THREE.MeshBasicNodeMaterial({ color: colorHex });
  mat.transparent = true;
  mat.depthWrite = false;
  mat.side = THREE.DoubleSide;
  mat.blending = THREE.AdditiveBlending;
  const fade = uniform(1);
  // Half-sphere UVs: v runs 0 at the equator rim → 1 at the pole. Strong at
  // the rim, gone by halfway up.
  mat.opacityNode = Fn(() => smoothstep(float(0.55), float(0.05), uv().y).mul(float(0.22)).mul(fade))();
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set(x, y, z);
  return track(mesh, life, (m, t) => {
    // Full strength until the last 15% — a field that fades the whole time
    // reads as expired while it is still suppressing.
    fade.value = (t > 0.85 ? (1 - t) / 0.15 : 1) * (1 + Math.sin(t * 22) * 0.2);
  });
}

// ─── The six archetypes ─────────────────────────────────────────
//
// ctx: { ability, abilityId, characterId, color, origin {x,y,z}, groundY,
//        yaw, fwd {x,z}, windupMs, recoveryMs, tail }
// `origin.y` is the capsule centre; ground shapes use ctx.groundY + FX_LIFT.
// Anticipations run during the wind-up; strikes run AT resolve, from the
// anchor's live position for body abilities.

const ARCHETYPES = {
  melee_cone: {
    anticipate(ctx) {
      const { origin, color, windupMs } = ctx;
      // A glint at the weapon hand — the cheap read for the shortest wind-up
      // in the game. Inward shards only when the wind-up is long enough for
      // anyone to see them arrive.
      chargeStar(origin.x, origin.y + 0.5, origin.z, 0.8, color, windupMs);
      if (windupMs >= 200) gatherShards(ctx, 1.6, nCount(6));
    },
    strike(ctx) {
      const { ability, origin, groundY, color, yaw, fwd, tail } = ctx;
      const range = ability.hit.range;
      const { mesh, u } = slashArc(range, ability.hit.halfAngleDeg, color);
      // Chest height, tipped slightly toward the camera so the arc keeps area
      // at the 55° pitch instead of foreshortening to a sliver.
      flatOnGround(mesh, origin.x, origin.y + 0.1, origin.z, yaw);
      mesh.rotation.x += 0.22;
      mesh.renderOrder = 7;
      const sweepFrac = 0.45; // wipe crosses the sector, then the trail fades
      track(mesh, 130 + tail * 0.7, (m, t) => {
        u.progress.value = Math.min(t / sweepFrac, 1);
        u.fade.value = holdFade(t, 0.5);
      });
      const tipX = origin.x + fwd.x * range * 0.75;
      const tipZ = origin.z + fwd.z * range * 0.75;
      spawnParticles({ x: tipX, y: origin.y + 0.2, z: tipZ }, color, nCount(9), 6.5, {
        shape: 'shard',
        lifetime: 380,
        size: 0.22,
        dir: { x: fwd.x * 0.7, y: 0.1, z: fwd.z * 0.7 },
        gravity: 7,
      });
    },
  },

  sphere: {
    anticipate(ctx) {
      const { ability, origin, groundY, color, windupMs } = ctx;
      // The gather draws the ability's OWN radius contracting onto the caster
      // — "this whole circle, soon". Nearest-target abilities (chain, swap)
      // skip it: their telegraph rim + reticle already say the honest thing,
      // and a contracting AoE ring would say the dishonest one.
      if (!nearestOnly(ctx.ability)) {
        gatherRing(origin.x, groundY + FX_LIFT, origin.z, ability.hit.radius, color, windupMs);
      }
      chargeStar(origin.x, origin.y + 0.9, origin.z, 1.4, color, windupMs);
      gatherShards(ctx, Math.min(ability.hit.radius * 0.5, 4), nCount(10));
    },
    strike(ctx) {
      const { ability, origin, groundY, color, tail } = ctx;
      const radius = ability.hit.radius;
      const gy = groundY + FX_LIFT;
      if (nearestOnly(ability)) {
        // Chain Lightning / Robotaxi strike ONE target — the arcs and swap
        // pillars land per `hit` event. Here: only the caster's own discharge.
        pillar(origin.x, groundY, origin.z, 0.9, 4.5, color, 420);
        popStar(origin.x, origin.y + 1.2, origin.z, 2.4, color, 260);
        return;
      }
      const pull = (ability.effects ?? []).some((e) => e.kind === 'pull');
      popRing(origin.x, gy, origin.z, radius, color, 200 + tail, { reverse: pull });
      popStar(origin.x, origin.y + 0.4, origin.z, Math.min(radius * 0.55, 3.2), color, 280);
      if (pull) {
        // Debris rushes INTO the well — the shape has to say "pulls".
        spawnParticles({ x: origin.x, y: gy + 0.2, z: origin.z }, color, nCount(16), radius * 1.1, {
          shape: 'shard',
          emitRadius: radius * 0.9,
          inward: true,
          lifetime: 620,
          gravity: 0,
          size: 0.24,
          blending: 'additive',
        });
      } else {
        // Slam: chunky debris thrown up and out, plus a dust ring for weight.
        spawnParticles({ x: origin.x, y: gy + 0.3, z: origin.z }, color, nCount(18), radius * 1.4, {
          shape: 'shard',
          emitRadius: radius * 0.3,
          lifetime: 700,
          upBias: 1.3,
          gravity: 14,
          size: 0.3,
        });
        if (luxe()) {
          spawnParticles({ x: origin.x, y: gy + 0.15, z: origin.z }, 0x9a8a76, nCount(12), radius, {
            emitRadius: radius * 0.55,
            lifetime: 850,
            upBias: 0.4,
            gravity: 3,
            size: 0.34,
          });
        }
      }
    },
  },

  dash_line: {
    anticipate(ctx) {
      const { origin, color, windupMs } = ctx;
      // The coil: energy converging on the caster plus a brightening glint.
      // The ground line itself is the telegraph's job — exact, already drawn.
      chargeStar(origin.x, origin.y + 0.4, origin.z, 1.1, color, windupMs);
      gatherShards(ctx, 2.0, nCount(8));
    },
    strike(ctx) {
      const { ability, origin, groundY, color, yaw, fwd, tail } = ctx;
      const len = ability.selfMove && ability.selfMove > 0 ? ability.selfMove : ability.hit.length;
      const width = ability.hit.width;
      const gy = groundY + FX_LIFT;
      const stun = (ability.effects ?? []).some((e) => e.kind === 'stun');
      // The line at its authored length and width, pulse head travelling the
      // whole length. Fault Line's pulse takes ~300ms (the ground cracking
      // open); Blink Strike's ~110ms (a body moving at dash speed).
      const pulseMs = stun ? 300 : 110;
      const life = pulseMs + 220 + tail;
      const { mesh, u } = lineWall(len, width, color);
      flatOnGround(mesh, origin.x + fwd.x * (len / 2), gy, origin.z + fwd.z * (len / 2), yaw);
      mesh.renderOrder = 6;
      track(mesh, life, (m, t) => {
        u.progress.value = Math.min((t * life) / pulseMs, 1);
        u.fade.value = holdFade(t, 0.55);
      });
      popStar(origin.x + fwd.x * len, origin.y, origin.z + fwd.z * len, 1.8, color, 260);
      if (stun) {
        // Fault Line: eruptions march down the crack on the pulse's own clock.
        const steps = luxe() ? 5 : 3;
        for (let i = 1; i <= steps; i++) {
          const f = i / steps;
          const ex = origin.x + fwd.x * len * f;
          const ez = origin.z + fwd.z * len * f;
          _pending.push({
            at: performance.now() + pulseMs * f,
            fire: () => {
              pillar(ex, groundY, ez, 0.5, 2.6, color, 380);
              spawnParticles({ x: ex, y: gy + 0.2, z: ez }, color, nCount(6), 7, {
                shape: 'shard',
                lifetime: 620,
                upBias: 2,
                gravity: 16,
                size: 0.3,
              });
            },
          });
        }
      } else {
        // Blink Strike: departure and arrival columns bracket the dash.
        pillar(origin.x, groundY, origin.z, 0.5, 2.2, color, 300);
        pillar(origin.x + fwd.x * len, groundY, origin.z + fwd.z * len, 0.5, 2.2, color, 340);
        spawnParticles(
          { x: origin.x + fwd.x * len * 0.5, y: origin.y, z: origin.z + fwd.z * len * 0.5 },
          color,
          nCount(10),
          5,
          { shape: 'shard', lifetime: 450, gravity: 2, size: 0.2, blending: 'additive' }
        );
      }
    },
  },

  projectile: {
    anticipate(ctx) {
      const { origin, color, windupMs, fwd } = ctx;
      // The charge gathers at the MUZZLE, down the frozen aim — from overhead,
      // where the glint sits is the aim readout.
      const mx = origin.x + fwd.x * 0.9;
      const mz = origin.z + fwd.z * 0.9;
      chargeStar(mx, origin.y + 0.5, mz, 1.0, color, windupMs);
    },
    strike(ctx) {
      const { ability, origin, color, fwd } = ctx;
      const mx = origin.x + fwd.x * 0.9;
      const mz = origin.z + fwd.z * 0.9;
      popStar(mx, origin.y + 0.5, mz, 1.6, color, 200);
      spawnParticles({ x: mx, y: origin.y + 0.5, z: mz }, color, nCount(6), 5, {
        shape: 'shard',
        lifetime: 300,
        dir: { x: fwd.x, y: 0.15, z: fwd.z },
        gravity: 4,
        size: 0.16,
        blending: 'additive',
      });
      // The shell itself flies from resolve — the sim spawns it at resolve.
      ballisticTracer(origin, fwd, ability, color, ctx.characterId);
    },
  },

  ground_zone: {
    anticipate(ctx) {
      const { ability, origin, groundY, color, windupMs, fwd, abilityId } = ctx;
      const off = GROUND_ZONE_OFFSET[abilityId] ?? 0;
      const zx = origin.x + fwd.x * off;
      const zz = origin.z + fwd.z * off;
      // The glyph ring assembles OVER the zone it will own: spinning in from
      // above, contracting onto the authored radius as the wind-up runs. The
      // exact filled telegraph underneath is Telegraphs.js's job.
      const { mesh, u } = zoneRing(color, { radius: ability.hit.radius });
      flatOnGround(mesh, zx, groundY + FX_LIFT + 1.6, zz);
      mesh.renderOrder = 7;
      const radius = ability.hit.radius;
      track(mesh, windupMs + 60, (m, t) => {
        const k = easeOutCubic(Math.min(t * (windupMs + 60) / windupMs, 1));
        m.scale.setScalar(Math.max(0.05, radius * (0.4 + 0.6 * k)));
        m.position.y = groundY + FX_LIFT + 1.6 * (1 - k);
        u.fade.value = 0.25 + 0.55 * k;
      });
    },
    strike(ctx) {
      const { ability, origin, groundY, color, fwd, abilityId, tail } = ctx;
      const radius = ability.hit.radius;
      const durationMs = ability.hit.durationMs;
      const off = GROUND_ZONE_OFFSET[abilityId] ?? 0;
      const zx = origin.x + fwd.x * off;
      const zz = origin.z + fwd.z * off;
      const gy = groundY + FX_LIFT;
      // The stamp: the ring pops at the exact radius…
      popRing(zx, gy + DECAL_STACK, zz, radius, color, 240 + tail);
      // …then the duration marker owns the ground for the zone's whole life,
      // holding full strength until the last 15% (the runeCircle doctrine).
      const { mesh, u } = zoneRing(color, { radius });
      flatOnGround(mesh, zx, gy, zz);
      mesh.scale.setScalar(radius);
      mesh.renderOrder = 5;
      track(mesh, durationMs, (m, t) => {
        u.fade.value = t > 0.85 ? (1 - t) / 0.15 : 1;
      });
      const suppress = (ability.effects ?? []).some((e) => e.kind === 'suppress_abilities');
      if (suppress) dome(zx, groundY, zz, radius, color, durationMs);
      const repeat = (ability.effects ?? []).find((e) => e.kind === 'repeat');
      if (repeat && luxe()) {
        // Starlink's shells: decorative streaks INSIDE the true circle. The
        // sim rolls its own impact points — these are flourish, deliberately
        // small and ringless so they never claim a specific splash footprint.
        for (let i = 0; i < repeat.count; i++) {
          const a = Math.random() * Math.PI * 2;
          const r = Math.random() * radius * 0.75;
          const sx = zx + Math.sin(a) * r;
          const sz = zz + Math.cos(a) * r;
          _pending.push({
            at: performance.now() + 200 + i * repeat.intervalMs,
            fire: () => shellDrop(sx, groundY, sz, color),
          });
        }
      }
    },
  },

  self: {
    anticipate(ctx) {
      const { origin, groundY, color, windupMs } = ctx;
      // Inward, always inward — nothing leaves the caster because nothing
      // leaves the caster. The hex contracts onto the body.
      const { mesh, u } = hexRing(color);
      flatOnGround(mesh, origin.x, groundY + FX_LIFT, origin.z);
      mesh.renderOrder = 6;
      track(mesh, windupMs + 60, (m, t) => {
        const k = Math.min(t * (windupMs + 60) / windupMs, 1);
        m.scale.setScalar(Math.max(0.4, 2.6 - easeOutCubic(k) * 1.5));
        u.fade.value = 0.3 + 0.6 * k;
      });
      gatherShards(ctx, 1.8, nCount(8));
    },
    strike(ctx) {
      const { ability, origin, groundY, color, tail } = ctx;
      const gy = groundY + FX_LIFT;
      // The hex pop at the feet…
      const { mesh, u } = hexRing(color);
      flatOnGround(mesh, origin.x, gy, origin.z);
      mesh.renderOrder = 6;
      track(mesh, 300 + tail, (m, t) => {
        m.scale.setScalar(Math.max(0.3, 1.9 * easeOutCubic(Math.min(t * 2.2, 1))));
        u.fade.value = holdFade(t, 0.4);
      });
      // …rising sparks…
      spawnParticles({ x: origin.x, y: origin.y - 0.4, z: origin.z }, color, nCount(12), 3.2, {
        shape: 'shard',
        emitRadius: 0.8,
        lifetime: 700,
        upBias: 1.6,
        gravity: -1.5,
        size: 0.18,
        blending: 'additive',
      });
      // …and, for a buff that STAYS (speed / damage reduction / hover), the
      // hexagonal shell for the buff's own duration. Decoy and invisibility
      // skip it — a 4s shell on a decoy's caster, or on someone whose whole
      // ability is being unseeable, would be misinformation.
      const buff = (ability.effects ?? []).find(
        (e) => e.kind === 'speed' || e.kind === 'damage_reduction' || e.kind === 'hover'
      );
      if (buff?.durationMs) {
        // The shell rides its OWN follow anchor: the cast anchor freezes at
        // resolve (strike shapes must not slide), but a speed shell nailed to
        // the cast spot while its owner sprints away would be a shell around
        // nobody. The buff is ON THE BODY for durationMs; so is its shell.
        const prev = _buildAnchor;
        _buildAnchor = beginBodyAnchor(`${ctx.abilityId}-shell`, ctx.casterId, origin, Infinity, 0);
        const { mesh: shell, u: su } = hexShell(color, 1.15, 2.7);
        shell.position.set(origin.x, origin.y, origin.z);
        shell.renderOrder = 7;
        track(shell, buff.durationMs, (m, t) => {
          m.rotation.y += 0.01;
          su.fade.value = t > 0.85 ? (1 - t) / 0.15 : 1;
        });
        _buildAnchor = prev;
      }
    },
  },
};

/** Chain Lightning / Robotaxi: the sim strikes ONE nearest target. */
function nearestOnly(ability) {
  return (ability.effects ?? []).some((e) => e.kind === 'chain' || e.kind === 'swap_position');
}

/** The anticipation gather: shards converging on the caster, timed to arrive
 *  on the strike beat. Approximate by design — it is flourish, not geometry. */
function gatherShards(ctx, radius, count) {
  const { origin, color, windupMs } = ctx;
  const speed = radius / Math.max(windupMs / 1000, 0.12);
  spawnParticles({ x: origin.x, y: origin.y - 0.3, z: origin.z }, color, count, speed, {
    shape: 'shard',
    emitRadius: radius,
    inward: true,
    lifetime: Math.min(windupMs, 700),
    gravity: 0,
    size: 0.15,
    blending: 'additive',
  });
}

/** A Starlink shell: a falling streak and a small ringless pop. */
function shellDrop(x, groundY, z, colorHex) {
  const drop = 12;
  const fallMs = 240;
  const { mesh, u } = starFlash(colorHex, { additive: true });
  mesh.position.set(x, groundY + drop, z);
  mesh.scale.setScalar(0.9);
  track(mesh, fallMs, (m, t) => {
    m.position.y = groundY + drop * (1 - t * t);
    u.fade.value = 1;
  });
  _pending.push({
    at: performance.now() + fallMs,
    fire: () => {
      popStar(x, groundY + 0.5, z, 1.5, colorHex, 220);
      spawnParticles({ x, y: groundY + 0.3, z }, colorHex, nCount(5), 5, {
        shape: 'shard',
        lifetime: 420,
        upBias: 1.4,
        gravity: 12,
        size: 0.22,
      });
    },
  });
}

// ─── Public entry points ────────────────────────────────────────

/**
 * The cast event, at the START of the wind-up. Anticipation plays now;
 * the strike is scheduled onto the wind-up clock and fires when the sim
 * resolves the hit. `origin` is where main.js decided this cast lives (event
 * x/z for world-anchored, the caster's rendered transform for body-anchored).
 */
export function playAbilityCast(abilityId, characterId, casterId, origin, yaw) {
  const ability = findAbility(characterId, abilityId);
  const color = characterColorHex(characterId);
  const fwd = forwardFromYaw(yaw ?? 0);

  // The universal dash: no AbilityDef, no hit to agree with. A departure
  // burst is always true; a directional streak would point the wrong way on
  // every sideways dash (the event carries only yaw).
  if (!ability) {
    if (abilityId === 'dash') {
      spawnParticles(origin, 0xbdefff, nCount(12), 5.5, {
        shape: 'shard',
        lifetime: 420,
        blending: 'additive',
        size: 0.2,
        gravity: 1,
      });
      popRing(origin.x, origin.y - PLAYER_HALF_HEIGHT + FX_LIFT, origin.z, 1.9, 0x9ff0ff, 320);
    } else {
      spawnParticles(origin, color, nCount(10), 5, { shape: 'shard', lifetime: 500, blending: 'additive' });
    }
    return;
  }

  const arch = ARCHETYPES[ability.hit.kind];
  if (!arch) return;

  const windupMs = ability.windupMs ?? 0;
  const ctx = {
    ability,
    abilityId,
    characterId,
    casterId,
    color,
    origin: { x: origin.x, y: origin.y, z: origin.z },
    groundY: origin.y - PLAYER_HALF_HEIGHT,
    yaw: yaw ?? 0,
    fwd,
    windupMs,
    recoveryMs: ability.recoveryMs ?? 0,
    tail: Math.max(ability.recoveryMs ?? 0, 160),
  };

  // Body-anchored casts ride the caster's rendered transform for the whole
  // three-beat envelope; `holdUntil` keeps the anchor alive until the strike
  // has spawned into it. Blink Strike freezes at the dash (see beginBodyAnchor).
  let anchor = null;
  if (abilityAnchor(ability) === 'body') {
    anchor = beginBodyAnchor(
      abilityId,
      casterId,
      ctx.origin,
      abilityId === 'blink_strike' ? windupMs : Infinity,
      windupMs + 300
    );
  }

  if (windupMs > 0) {
    _buildAnchor = anchor;
    try {
      arch.anticipate?.(ctx);
    } finally {
      _buildAnchor = null;
    }
    _pending.push({
      at: performance.now() + windupMs,
      fire: () => fireStrike(arch, ctx, anchor),
    });
  } else {
    // Zero wind-up: no anticipation beat exists to draw. Strike now.
    fireStrike(arch, ctx, anchor);
  }
}

/**
 * The strike beat. For a body ability the origin is re-read from the anchor's
 * LIVE position — the sim resolves from where the caster IS at the resolve
 * tick, not where the cast began (a walking Monolith's slam lands under his
 * feet, not 1.5m behind them).
 */
function fireStrike(arch, ctx, anchor) {
  if (anchor) {
    ctx.origin = {
      x: anchor.group.position.x,
      y: anchor.group.position.y,
      z: anchor.group.position.z,
    };
    ctx.groundY = ctx.origin.y - PLAYER_HALF_HEIGHT;
  }
  _buildAnchor = anchor && _anchors.includes(anchor) ? anchor : null;
  try {
    arch.strike?.(ctx);
  } finally {
    _buildAnchor = null;
    // The sim resolved the hit HERE. A strike shape that kept following a
    // walking caster would drag the damage marker off the ground actually
    // damaged — the same reason Telegraphs freeze their decals at resolve.
    // (Buff shells that must keep following ride their own anchor — see
    // the self archetype.)
    if (anchor) anchor.freezeAt = 0;
  }
}

/** Impact VFX for a resolved hit. Driven by the server's `hit` event. */
export function playHitVFX(abilityId, characterId, point, heavy) {
  const color = characterColorHex(characterId);
  popStar(point.x, point.y + 0.2, point.z, heavy ? 2.1 : 1.2, color, heavy ? 300 : 220);
  spawnParticles(point, color, nCount(heavy ? 14 : 7), heavy ? 8 : 5.5, {
    shape: 'shard',
    lifetime: heavy ? 640 : 400,
    size: heavy ? 0.26 : 0.18,
    gravity: 10,
    drag: 0.06,
  });
  if (heavy) {
    popRing(point.x, point.y - PLAYER_HALF_HEIGHT + FX_LIFT, point.z, 2.2, color, 340);
  }
}

// ─── Hit flash on the struck body ───────────────────────────────
//
// The impact star says WHERE the hit landed; the body flash says WHO was hit.
// Without it, a hit on a moving target reads as a puff near a character
// rather than damage on them — v1 flashed the victim and it was load-bearing.

const HIT_FLASH_MS = 80;
/** playerId -> { mesh, born }. Re-hitting restarts the pulse. */
const _hitFlashes = new Map();

/**
 * Pulse the struck player's body white for one beat. Driven by the server's
 * `hit` event (the caller passes the event's targetId) — purely presentational.
 *
 * Goes through ToonMaterials.setFlashAmount, which mixes a per-material TSL
 * uniform toward white. Never touch `.emissive` here: these are node materials
 * with a `colorNode`, and the scalar emissive path is not consulted.
 */
export function flashHitTarget(playerId) {
  if (typeof ToonMaterials.setFlashAmount !== 'function') return;
  const mesh = playerId === session.userId ? localPlayer.mesh : remotePlayers.get(playerId);
  if (!mesh) return;
  _hitFlashes.set(playerId, { mesh, born: performance.now() });
  ToonMaterials.setFlashAmount(mesh, 1);
}

function updateHitFlashes(now) {
  if (_hitFlashes.size === 0) return;
  for (const [id, flash] of _hitFlashes) {
    const t = (now - flash.born) / HIT_FLASH_MS;
    if (t >= 1) {
      ToonMaterials.setFlashAmount(flash.mesh, 0);
      _hitFlashes.delete(id);
    } else {
      ToonMaterials.setFlashAmount(flash.mesh, 1 - t);
    }
  }
}

/** Chain Lightning's arcs: one jagged bolt per link, between resolved targets. */
export function playChainArc(from, to, characterId) {
  const color = characterColorHex(characterId);
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dz = to.z - from.z;
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz) || 0.001;
  const { mesh, u } = boltBeam(color);
  mesh.scale.set(1.1, len, 1);
  mesh.position.set(from.x + dx / 2, from.y + dy / 2, from.z + dz / 2);
  mesh.lookAt(to.x, to.y, to.z);
  mesh.rotateX(Math.PI / 2);
  track(mesh, 260, (m, t) => {
    u.fade.value = 1 - t;
    // Re-seed twice over the bolt's life so the arc flickers like current.
    if (t > 0.5 && !m.userData.reseeded) {
      m.userData.reseeded = true;
      u.seed.value = Math.random() * 10;
    }
  });
  popStar(to.x, to.y + 0.2, to.z, 1.4, 0xffffff, 220, { additive: true });
}

/** Elimination: the pot settles. Ring, pillar, and a fountain of the fallen
 *  character's own colour — the toy's scatter (pose.js) does the body. */
export function playEliminationVFX(point, characterId) {
  const color = characterColorHex(characterId);
  popRing(point.x, point.y - PLAYER_HALF_HEIGHT + FX_LIFT, point.z, 3.2, color, 620);
  spawnParticles(point, color, nCount(26), 7, {
    shape: 'shard',
    lifetime: 1100,
    size: 0.26,
    gravity: 6,
    drag: 0.04,
    upBias: 1.4,
  });
  pillar(point.x, point.y - PLAYER_HALF_HEIGHT, point.z, 0.8, 3.4, color, 700);
}

// ─── Ballistic tracer ───────────────────────────────────────────

/**
 * A predicted shell for lobbed projectiles. Snapshots do not carry projectile
 * positions, so without this the shot is invisible from muzzle to impact and
 * the victim's first cue is the damage number. The path is precomputed with
 * the sim's own tick math (gravity before move, spawn offset, range spent
 * after moving) from the cast event's frozen yaw. Fired at STRIKE, because the
 * sim spawns the shell at resolve. Purely presentational: on an open lane it
 * lands where the real shell lands; a wall mid-flight stops the real one early
 * and this one flies through — the `hit` event still marks the true impact.
 */
function ballisticTracer(origin, fwd, ability, colorHex, characterId) {
  const shape = ability.hit;
  const floorY = origin.y - PLAYER_HALF_HEIGHT;

  const pts = [];
  let vy = shape.speed * PROJECTILE_LAUNCH_SIN;
  const vh = shape.speed * PROJECTILE_LAUNCH_COS;
  let x = origin.x + fwd.x * PROJECTILE_SPAWN_OFFSET;
  let y = origin.y + 0.4;
  let z = origin.z + fwd.z * PROJECTILE_SPAWN_OFFSET;
  let range = shape.maxRange;
  pts.push(x, y, z);
  for (let i = 0; i < 300 && y > floorY && range > 0; i++) {
    vy -= shape.gravity * DT;
    y += vy * DT;
    const step = vh * DT;
    x += fwd.x * step;
    z += fwd.z * step;
    range -= step;
    pts.push(x, y, z);
  }
  const flightMs = (pts.length / 3 - 1) * DT * 1000;

  // The shell is the reference's bolt: a hot star with a shard trail.
  const { mesh, u } = starFlash(colorHex, { additive: true });
  mesh.scale.setScalar(1.0);
  mesh.position.set(pts[0], pts[1], pts[2]);

  let burst = false;
  let trailCountdown = 0;
  const life = flightMs + 150;
  return track(mesh, life, (m, t) => {
    const flight = t * life;
    if (flight >= flightMs) {
      if (!burst) {
        burst = true;
        m.visible = false;
        const last = pts.length - 3;
        // The burst ring is the SPLASH RADIUS, not decoration — it marks the
        // ground band the detonation actually damages.
        popRing(pts[last], floorY + FX_LIFT, pts[last + 2], shape.radius, colorHex, 420);
        popStar(pts[last], floorY + 0.6, pts[last + 2], 2.0, colorHex, 260);
        spawnParticles({ x: pts[last], y: floorY + 0.3, z: pts[last + 2] }, colorHex, nCount(10), 6, {
          shape: 'shard',
          lifetime: 480,
          size: 0.22,
          upBias: 1.2,
          gravity: 12,
        });
      }
      return;
    }
    u.spin.value += 0.25;
    const s = flight / 1000 / DT;
    const i0 = Math.min(Math.floor(s), pts.length / 3 - 2) * 3;
    const f = Math.min(s - Math.floor(s), 1);
    m.position.set(
      pts[i0] + (pts[i0 + 3] - pts[i0]) * f,
      pts[i0 + 1] + (pts[i0 + 4] - pts[i0 + 1]) * f,
      pts[i0 + 2] + (pts[i0 + 5] - pts[i0 + 2]) * f
    );
    if (--trailCountdown <= 0) {
      trailCountdown = 4;
      spawnParticles(m.position, colorHex, 1, 0.6, {
        shape: 'shard',
        lifetime: 280,
        blending: 'additive',
        size: 0.14,
      });
    }
  });
}

function findAbility(characterId, abilityId) {
  const def = CHARACTERS[characterId];
  if (!def) return null;
  for (const slot of ['basic', 'skill1', 'skill2', 'ultimate']) {
    if (def.abilities[slot].id === abilityId) return def.abilities[slot];
  }
  return null;
}

// ─── Shader prewarm hook ────────────────────────────────────────

/**
 * Fires every material graph this module can produce, immediately (no beat
 * scheduling — prewarm needs the materials to EXIST when compileAsync runs).
 * One representative ability per archetype covers every builder.
 */
export function prewarmAbilityVFX(origin) {
  const reps = [
    ['basic', 'volt'], // melee_cone → slashArc, stars, shards
    ['seismic_slam', 'monolith'], // sphere → burstRing, pillar
    ['fault_line', 'monolith'], // dash_line → lineWall
    ['arc_shot', 'talon'], // projectile → tracer star
    ['barrage', 'talon'], // ground_zone → zoneRing, dome-free path
    ['null_zone', 'hex'], // ground_zone + dome
    ['bulwark', 'monolith'], // self → hexRing, hexShell
  ];
  for (const [abilityId, characterId] of reps) {
    const ability = findAbility(characterId, abilityId);
    if (!ability) continue;
    const ctx = {
      ability,
      abilityId,
      characterId,
      casterId: '__prewarm__',
      color: characterColorHex(characterId),
      origin: { ...origin },
      groundY: origin.y - PLAYER_HALF_HEIGHT,
      yaw: 0,
      fwd: { x: 0, z: 1 },
      windupMs: ability.windupMs ?? 0,
      recoveryMs: ability.recoveryMs ?? 0,
      tail: 160,
    };
    const arch = ARCHETYPES[ability.hit.kind];
    if (ctx.windupMs > 0) arch.anticipate?.(ctx);
    arch.strike?.(ctx);
  }
  playChainArc(origin, { x: origin.x + 2, y: origin.y, z: origin.z }, 'volt');
  playHitVFX('basic', 'volt', origin, true);
}

// ─── Frame update ───────────────────────────────────────────────

export function updateAbilityVFX() {
  const now = performance.now();
  updateHitFlashes(now);
  // Strike beats fire on the wind-up clock — before mesh updates, so a strike
  // spawned this frame renders this frame.
  for (let i = _pending.length - 1; i >= 0; i--) {
    if (now >= _pending[i].at) {
      const [p] = _pending.splice(i, 1);
      p.fire();
    }
  }
  for (let i = _live.length - 1; i >= 0; i--) {
    const fx = _live[i];
    const t = (now - fx.born) / fx.life;
    if (t >= 1) {
      // removeFromParent, not _scene.remove: body-anchored meshes live inside
      // an anchor group. Shared geometry (CelShapes cache, Sprite's plane) is
      // never disposed — only per-effect materials are.
      fx.mesh.removeFromParent();
      if (!fx.sharedGeo) fx.mesh.geometry.dispose();
      fx.mesh.material.dispose();
      _live.splice(i, 1);
      continue;
    }
    fx.update?.(fx.mesh, t);
  }
  updateAnchors(now);
}

export function clearAbilityVFX() {
  _pending.length = 0;
  for (const fx of _live) {
    fx.mesh.removeFromParent();
    if (!fx.sharedGeo) fx.mesh.geometry.dispose();
    fx.mesh.material.dispose();
  }
  _live.length = 0;
  for (const a of _anchors) _scene.remove(a.group);
  _anchors.length = 0;
  _buildAnchor = null;
  if (typeof ToonMaterials.setFlashAmount === 'function') {
    for (const flash of _hitFlashes.values()) ToonMaterials.setFlashAmount(flash.mesh, 0);
  }
  _hitFlashes.clear();
}
